import { Link } from 'react-router-dom';
import { GeoAlt, Speedometer2, Droplet, Gear, CalendarEvent } from 'react-bootstrap-icons';
import CarImage from '../../common/ui/CarImage';
import language from '../../data/language';
import { currentLanguage } from '../../common/ui/notify';
import {
    carTitle, coverImage, display, formatDate, formatMileage, formatPrice, isBlank, translateSpec
} from '../../common/format';

/**
 * A single market listing. Every field is optional: whatever the database is
 * missing simply does not render, so a half-filled car never shows "undefined"
 * and never breaks the grid.
 */
const CarCard = ({car}) => {
    const lang = currentLanguage();
    const strings = language[lang].market;
    const sold = Number(car.sold) === 1;
    const inAuction = Number(car.in_auction) === 1;
    // Georgian locale gets the Georgian sticker; every other language the English one.
    const auctionSticker = lang === "geo" ? "/auction-sticker-ka.svg" : "/auction-sticker-en.svg";

    // Engine and fuel share one line — show whichever exists.
    const engineLine = [
        isBlank(car.engine) ? null : `${car.engine}${String(car.engine).match(/[a-zA-Z]/) ? "" : "L"}`,
        isBlank(car.fuel) ? null : translateSpec("fuel", car.fuel, lang)
    ].filter(Boolean).join(" · ");

    const specs = [
        {key: 'mileage', icon: <Speedometer2 />, value: isBlank(car.millage) ? null : formatMileage(car.millage)},
        {key: 'engine', icon: <Droplet />, value: engineLine || null},
        {key: 'transmission', icon: <Gear />, value: isBlank(car.transmission) ? null : translateSpec("transmission", car.transmission, lang)},
        {key: 'year', icon: <CalendarEvent />, value: isBlank(car.year) ? null : display(car.year)}
    ].filter((spec) => spec.value !== null);

    return (
        <article className="market-card">
            <Link to={`/car/${car.id}`} className="market-card-media" aria-label={carTitle(car)}>
                <CarImage src={coverImage(car)} alt={carTitle(car)} className="market-card-image" />
                <span className={`cx-badge ${sold ? "cx-badge-sold" : "cx-badge-sale"} market-card-status`}>
                    {sold ? strings.status_sold : strings.status_for_sale}
                </span>
                {inAuction && (
                    <img src={auctionSticker} alt="Auction" className="market-card-auction-sticker" aria-hidden="true" />
                )}
            </Link>

            <div className="market-card-body">
                <div className="market-card-heading">
                    <h3 className="market-card-title">
                        <Link to={`/car/${car.id}`}>{carTitle(car)}</Link>
                    </h3>
                    <p className="market-card-price">{formatPrice(car.price, car.currency)}</p>
                </div>

                {specs.length > 0 && (
                    <ul className="market-card-specs">
                        {specs.map((spec) => (
                            <li key={spec.key}>
                                <span className="market-card-spec-icon" aria-hidden="true">{spec.icon}</span>
                                <span>{spec.value}</span>
                            </li>
                        ))}
                    </ul>
                )}

                <div className="market-card-footer">
                    <div className="market-card-meta">
                        {!isBlank(car.location) && (
                            <span className="market-card-location" title={translateSpec("location", display(car.location), lang)}>
                                <GeoAlt aria-hidden="true" /> {translateSpec("location", display(car.location), lang)}
                            </span>
                        )}
                        {!isBlank(car.create_date) && (
                            <span className="market-card-date">
                                {strings.published}: {formatDate(car.create_date)}
                            </span>
                        )}
                    </div>
                    <Link to={`/car/${car.id}`} className="cx-btn cx-btn-primary cx-btn-sm market-card-cta">
                        {strings.view_details}
                    </Link>
                </div>
            </div>
        </article>
    );
};

export default CarCard;
